import { createContext, type ReactNode, useContext } from 'react';
import type { ResponseError } from 'superagent';

import { USER_ROLES } from '@didaskill/domain/users/constants';
import type { EmployeeViewFull } from '@didaskill/domain/users/types';
import { useMemoizedBundle } from '@didaskill/ui/utils';

import { useUser } from '../hooks/queries/user';

export type AuthContextValue = {
  user: EmployeeViewFull | undefined;
  isLoading: boolean;
  error?: Error | ResponseError;
  isAdmin: boolean;
  isEditor: boolean;
  isExternalEditor: boolean;
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isLoading: false,
  error: null,
  isAdmin: false,
  isEditor: false,
  isExternalEditor: false,
});

export type AuthProviderProps = {
  children?: ReactNode;
};

const AuthProvider = ({ children = null }: AuthProviderProps) => {
  const {
    data: user,
    isLoading,
    error,
  } = useUser({
    throwOnError: false,
  });

  const isAdmin = user?.roles?.includes(USER_ROLES.ADMIN);
  const isEditor = user?.roles?.includes(USER_ROLES.EDITOR);
  const isExternalEditor = user?.roles?.includes(USER_ROLES.EXTERNAL_EDITOR);

  const contextValue = useMemoizedBundle<AuthContextValue>({
    user,
    isLoading,
    error,
    isAdmin,
    isEditor,
    isExternalEditor,
  });

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export { AuthProvider, useAuth };
